// src/pages/teachers/index.js
import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  alpha, 
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  Paper,
  Breadcrumbs,
  Link,
  Stack
} from '@mui/material';
import { 
  Add as AddIcon, 
  People as PeopleIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  Devices as DevicesIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { PageHeader } from '../../components/layout';
import TabNavigation from '../../components/layout/TabNavigation';
import { useSearchParams } from '../../hooks/custom';

// Import teacher module pages
const ViewTeachers = React.lazy(() => import('./ViewTeachers'));
const AddTeacher = React.lazy(() => import('./AddTeacher'));
const CreateDeviceAccount = React.lazy(() => import('./CreateDeviceAccount'));
const AssignClasses = React.lazy(() => import('./AssignClasses'));

/**
 * Enhanced Teachers module main component with SafeLine theme
 * Handles routing and tab navigation for teacher management
 */
const TeachersModule = () => {
  const navigate = useNavigate();
  const { getParam, setParam } = useSearchParams();
  const location = useLocation();

  // Enhanced tab configuration with icons and descriptions
  const tabs = [
    { 
      label: 'View Teachers', 
      value: 'view', 
      path: 'view',
      icon: <ViewIcon />,
      description: 'Browse and manage existing teachers'
    },
    { 
      label: 'Add Teacher', 
      value: 'add', 
      path: 'add',
      icon: <PersonAddIcon />,
      description: 'Create new teacher accounts'
    },
    { 
      label: 'Device Accounts', 
      value: 'device-accounts', 
      path: 'device-accounts',
      icon: <DevicesIcon />,
      description: 'Generate device access credentials'
    },
    { 
      label: 'Assign Classes', 
      value: 'assign-classes', 
      path: 'assign-classes',
      icon: <AssignmentIcon />,
      description: 'Manage class assignments'
    }
  ];

  const pathSegment = location.pathname.split('/')[2]; // e.g., 'view', 'add', etc.
  const currentTab = tabs.find(tab => tab.path === pathSegment)?.value || 'view';
  
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setParam('tab', newValue);
    navigate(`/teachers/${tabs.find(tab => tab.value === newValue)?.path || 'view'}`);
  };
  
  // Add teacher button handler
  const handleAddTeacher = () => {
    navigate('/teachers/add');
    setParam('tab', 'add');
  };

  // Get current tab info
  const currentTabInfo = tabs.find(tab => tab.value === currentTab) || tabs[0];
  
  return (
    <Box sx={{ 
      backgroundColor: safeLineColors.lightGray, 
      minHeight: '100vh',
      pb: 4
    }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ 
            mb: 3,
            '& .MuiBreadcrumbs-separator': {
              color: safeLineColors.mutedGray
            }
          }}
        >
          <Link 
            underline="hover" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.mediumCharcoal,
              '&:hover': {
                color: safeLineColors.flameOrange
              }
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Dashboard
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.flameOrange,
              fontWeight: 'medium'
            }}
          >
            <SchoolIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Teachers
          </Typography>
        </Breadcrumbs>

        {/* Enhanced Header */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${safeLineColors.pureWhite} 0%, ${safeLineColors.orangeTint} 100%)`,
            border: `1px solid ${safeLineColors.coolGray}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                  mr: 3,
                  boxShadow: `0 8px 16px ${alpha(safeLineColors.flameOrange, 0.3)}`
                }}>
                  <PeopleIcon sx={{ color: safeLineColors.pureWhite, fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 700,
                      color: safeLineColors.darkCharcoal,
                      mb: 1,
                      background: `linear-gradient(135deg, ${safeLineColors.darkCharcoal}, ${safeLineColors.mediumCharcoal})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Teachers Management
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: safeLineColors.mediumCharcoal,
                      fontWeight: 400,
                      mb: 2
                    }}
                  >
                    Manage teacher accounts, permissions, and class assignments
                  </Typography>
                  
                  {/* Current Section Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={currentTabInfo.icon}
                      label={currentTabInfo.label}
                      sx={{
                        backgroundColor: safeLineColors.lightEmerald,
                        color: safeLineColors.forestGreen,
                        border: `1px solid ${safeLineColors.emerald}`,
                        fontWeight: 'medium',
                        '& .MuiChip-icon': {
                          color: safeLineColors.emerald
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                      {currentTabInfo.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<ViewIcon />}
                  onClick={() => {
                    navigate('/teachers/view');
                    setParam('tab', 'view');
                  }}
                  sx={{
                    borderRadius: 2,
                    borderColor: safeLineColors.coolGray,
                    color: safeLineColors.mediumCharcoal,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                      borderColor: safeLineColors.flameOrange,
                      backgroundColor: safeLineColors.orangeTint,
                      color: safeLineColors.flameOrange
                    }
                  }}
                >
                  View All
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTeacher}
                  sx={{
                    background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    boxShadow: `0 6px 20px ${alpha(safeLineColors.flameOrange, 0.3)}`,
                    fontWeight: 'bold',
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${safeLineColors.deepOrange}, ${safeLineColors.flameOrange})`,
                      boxShadow: `0 8px 25px ${alpha(safeLineColors.flameOrange, 0.4)}`,
                      transform: 'translateY(-2px)'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent, ${alpha(safeLineColors.pureWhite, 0.2)}, transparent)`,
                      transition: 'left 0.5s',
                    },
                    '&:hover::before': {
                      left: '100%'
                    }
                  }}
                >
                  Add New Teacher
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
        
        {/* Enhanced Tab Navigation */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            background: safeLineColors.pureWhite,
            border: `1px solid ${safeLineColors.coolGray}`,
            overflow: 'hidden'
          }}
        >
          <TabNavigation 
            tabs={tabs}
            value={currentTab}
            onChange={handleTabChange}
            baseUrl="/teachers"
            sx={{
              '& .MuiTabs-root': {
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `3px solid ${safeLineColors.coolGray}`,
                minHeight: 64
              },
              '& .MuiTab-root': {
                color: safeLineColors.mediumCharcoal,
                fontWeight: 'medium',
                textTransform: 'none',
                fontSize: '1rem',
                minHeight: 64,
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  color: safeLineColors.flameOrange,
                  fontWeight: 'bold',
                  backgroundColor: alpha(safeLineColors.flameOrange, 0.1)
                },
                '&:hover': {
                  backgroundColor: alpha(safeLineColors.flameOrange, 0.05),
                  color: safeLineColors.deepOrange
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: safeLineColors.flameOrange,
                height: 4,
                borderRadius: '2px 2px 0 0'
              }
            }}
          />
        </Paper>
        
        {/* Content Area with Enhanced Loading */}
        <Fade in timeout={300}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              background: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              minHeight: 500
            }}
          >
            <React.Suspense fallback={
              <Box sx={{ 
                p: 8, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400
              }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    border: `4px solid ${safeLineColors.orangeTint}`,
                    borderTop: `4px solid ${safeLineColors.flameOrange}`,
                    animation: 'spin 1s linear infinite',
                    mb: 3
                  }}
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: safeLineColors.mediumCharcoal,
                    fontWeight: 'medium',
                    mb: 1
                  }}
                >
                  Loading {currentTabInfo.label}...
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: safeLineColors.mutedGray
                  }}
                >
                  Please wait while we prepare your content
                </Typography>
              </Box>
            }>
              <Routes>
                <Route path="/" element={<Navigate to="/teachers/view" replace />} />
                <Route path="/view" element={<ViewTeachers />} />
                <Route path="/add" element={<AddTeacher />} />
                <Route path="/device-accounts" element={<CreateDeviceAccount />} />
                <Route path="/assign-classes" element={<AssignClasses />} />
                <Route path="*" element={<Navigate to="/teachers/view" replace />} />
              </Routes>
            </React.Suspense>
          </Paper>
        </Fade>
      </Container>

      {/* Add keyframe animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default TeachersModule;